import { useEffect, useState, type FormEvent, type ReactNode } from "react";
import { useNavigate } from "@tanstack/react-router";
import { Aperture, ShieldCheck } from "lucide-react";
import { signIn, getSession, homeForRole, type Role } from "@/lib/mock-auth";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";

interface DemoAccount {
  label: string;
  email: string;
}

interface Props {
  title: string;
  subtitle: string;
  audience: "studio" | "portal" | "admin";
  allowedRoles: Role[];
  demoAccounts: DemoAccount[];
  redirectTo?: string;
}

export function LoginCard({ title, subtitle, audience, allowedRoles, demoAccounts, redirectTo }: Props) {
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const s = getSession();
    if (s) navigate({ to: redirectTo ?? homeForRole(s.role), replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const onSubmit = (e: FormEvent) => {
    e.preventDefault();
    setError(null);
    setLoading(true);
    const result = signIn(email, password, allowedRoles);
    setLoading(false);
    if (!result.ok) {
      setError(result.error);
      return;
    }
    navigate({ to: redirectTo ?? homeForRole(result.user.role), replace: true });
  };

  const Icon: ReactNode = audience === "admin" ? <ShieldCheck className="size-5" /> : <Aperture className="size-5" />;
  const accent = audience === "admin" ? "bg-foreground text-background" : audience === "portal" ? "bg-brand text-background" : "bg-foreground text-background";

  return (
    <div className="flex min-h-screen items-center justify-center bg-background px-4 py-12">
      <div className="w-full max-w-md">
        <div className="mb-6 flex flex-col items-center gap-3 text-center">
          <div className={`flex size-10 items-center justify-center rounded-lg ${accent}`}>{Icon}</div>
          <div>
            <h1 className="text-xl font-semibold tracking-tight">{title}</h1>
            <p className="mt-1 text-sm text-muted-foreground">{subtitle}</p>
          </div>
        </div>

        <form
          onSubmit={onSubmit}
          className="rounded-xl bg-card p-6 ring-1 ring-border space-y-4"
        >
          <div className="space-y-1.5">
            <Label htmlFor="email">Email</Label>
            <Input
              id="email"
              type="email"
              autoComplete="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              placeholder="you@example.com"
            />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="password">Password</Label>
            <Input
              id="password"
              type="password"
              autoComplete="current-password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
            />
          </div>

          {error && (
            <div className="rounded-md bg-destructive/10 px-3 py-2 text-xs text-destructive">
              {error}
            </div>
          )}

          <Button type="submit" disabled={loading} className="w-full">
            {loading ? "Signing in…" : "Sign in"}
          </Button>
        </form>

        <div className="mt-6 rounded-lg border border-dashed border-border-strong bg-card/40 p-4">
          <p className="mb-2 font-mono text-[10px] uppercase tracking-widest text-muted-foreground">
            Demo accounts (password: <span className="text-foreground">password</span>)
          </p>
          <ul className="space-y-1">
            {demoAccounts.map((a) => (
              <li key={a.email} className="flex items-center justify-between gap-2 text-xs">
                <span className="text-muted-foreground">{a.label}</span>
                <button
                  type="button"
                  onClick={() => { setEmail(a.email); setPassword("password"); }}
                  className="font-mono text-foreground hover:underline"
                >
                  {a.email}
                </button>
              </li>
            ))}
          </ul>
        </div>
      </div>
    </div>
  );
}
